"use client";

import { useSession } from "next-auth/react";
import { useQuery } from "@tanstack/react-query";
import { useState, useCallback } from "react";
import { useRouter } from "next/navigation";
import { type ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Users, DollarSign, MousePointerClick, TrendingUp, Award, Copy, Check, Star } from "lucide-react";
import { DataTable, DataTableColumnHeader, type DataTableConfig } from "@admin/_components/data-table";
import { useApiList } from "@admin/_lib/api-hooks";
import { toast } from "sonner";
import type { ElementType } from "react";

interface ReferredUser {
  _id: string;
  name: string;
  email: string;
  phone: string;
  state?: string;
  city?: string;
  zip?: string;
  status?: number;
  total_amount_paid?: number;
  total_commission?: number;
  created_at?: number;
  createdon_datetime?: number;
}

interface AffiliateStats {
  // AFF-08: Angular reads response.result, totalAmount, numberOfDiviners, totalCommission, totalClick, totalConversion
  no_of_Sales?: number;
  result?: number;
  totalAmount?: number;
  number_of_click?: number;
  totalClick?: number;
  number_of_convertion?: number;
  totalConversion?: number;
  tottal_commission?: number;
  totalCommission?: number;
  numberOfDiviners?: number;
  number_of_diviner?: number;
}

function StatCard({
  title,
  value,
  icon: Icon,
  prefix = "",
  color = "#ffa800",
}: {
  title: string;
  value: string | number | undefined;
  icon: ElementType<{ className?: string }>;
  prefix?: string;
  color?: string;
}) {
  return (
    <Card style={{ borderColor: "hsl(240 20% 22%)" }}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="size-4" style={{ color }} />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold" style={{ color }}>
          {value === undefined ? (
            <span className="text-muted-foreground text-lg">—</span>
          ) : (
            `${prefix}${typeof value === "number" ? value.toLocaleString() : value}`
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// AFF-02: Copy referral link buttons
function CopyLinkButton({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success(`${label} copied!`);
    } catch {
      toast.error("Failed to copy");
    }
  }

  return (
    <button
      type="button"
      onClick={() => void copy()}
      className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs transition-colors hover:bg-white/5"
      style={{ border: "1px solid hsl(240 20% 26%)" }}
    >
      <span className="text-muted-foreground">{label}:</span>
      <span className="font-mono truncate max-w-[180px]" style={{ color: "#ffa800" }}>{value}</span>
      {copied ? (
        <Check className="size-3.5 shrink-0" style={{ color: "#16a085" }} />
      ) : (
        <Copy className="size-3.5 shrink-0 text-muted-foreground" />
      )}
    </button>
  );
}

export default function AffiliateDashboardPage() {
  const router = useRouter();
  const { data: session } = useSession();
  const userId = (session?.user as unknown as Record<string, unknown>)?._id as string | undefined;
  // affiliate_id field on the user object
  const affiliateId = (session?.user as unknown as Record<string, unknown>)?.affiliate_id as string | undefined;

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortField, setSortField] = useState("createdon_datetime");
  const [sortType, setSortType] = useState<"asc" | "desc">("desc");
  const [searchCondition, setSearchCondition] = useState<Record<string, unknown>>({});

  const baseUrl = process.env.NEXT_PUBLIC_API_CLOUDFRONT || "https://api.divineinfinitebeing.com/";
  const frontendUrl = process.env.NEXT_PUBLIC_FRONTEND_URL || "https://divineinfinitebeing.com/";

  const { data: stats } = useQuery<AffiliateStats>({
    queryKey: ["affiliate-stats", userId],
    queryFn: async () => {
      const res = await fetch(`${baseUrl}payment/count-transaction-by-affiliate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.token}`,
        },
        body: JSON.stringify({ affiliate_id: userId }),
      });
      const data = await res.json();
      // AFF-08: Handle both response shapes
      return data.results ?? data;
    },
    enabled: !!session?.token && !!userId,
  });

  // AFF-06: Filter referred users by related_affiliate_id = current user's _id
  const { data: referredUsers, isLoading } = useApiList<ReferredUser>(
    "user/user-fetch-by-affiliate_id",
    {
      page,
      limit,
      sortField,
      sortType,
      condition: {},
      searchcondition: {
        ...searchCondition,
        ...(userId ? { related_affiliate_id: userId } : {}),
      },
    },
  );

  const handleSort = useCallback((field: string, type: "asc" | "desc") => {
    setSortField(field);
    setSortType(type);
  }, []);

  const handleSearch = useCallback((field: string, value: string) => {
    setPage(1);
    setSearchCondition((prev) => {
      if (!value) { const next = { ...prev }; delete next[field]; return next; }
      return { ...prev, [field]: { $regex: value, $options: "i" } };
    });
  }, []);

  const handleFilter = useCallback((field: string, value: string) => {
    setPage(1);
    setSearchCondition((prev) => {
      if (!value) { const next = { ...prev }; delete next[field]; return next; }
      return { ...prev, [field]: field === "status" ? (value === "1" ? 1 : 0) : value };
    });
  }, []);

  // AFF-07: Add city, zip, status columns
  const columns: ColumnDef<ReferredUser>[] = [
    {
      accessorKey: "name",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />,
      enableSorting: true,
    },
    { accessorKey: "email", header: "Email" },
    { accessorKey: "phone", header: "Phone" },
    { accessorKey: "state", header: "State" },
    { accessorKey: "city", header: "City" },
    { accessorKey: "zip", header: "Zip" },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => (
        <Badge className="border text-[10px]" style={
          row.original.status === 1
            ? { background: "rgba(22,160,133,0.15)", color: "#16a085", borderColor: "rgba(22,160,133,0.3)" }
            : { background: "rgba(233,74,73,0.12)", color: "#e94a49", borderColor: "rgba(233,74,73,0.3)" }
        }>
          {row.original.status === 1 ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      accessorKey: "total_amount_paid",
      header: "Paid Amount",
      cell: ({ row }) => {
        const val = row.original.total_amount_paid;
        return val != null
          ? <span className="font-semibold" style={{ color: "#16a085" }}>{`$${val.toLocaleString()}`}</span>
          : "—";
      },
    },
    {
      accessorKey: "total_commission",
      header: "Commission",
      cell: ({ row }) => {
        const val = row.original.total_commission;
        return val != null ? `$${val.toLocaleString()}` : "—";
      },
    },
    {
      id: "created_at",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Joined" />,
      enableSorting: true,
      accessorFn: (row) => row.createdon_datetime ?? row.created_at,
      cell: ({ row }) => {
        const val = row.original.createdon_datetime ?? row.original.created_at;
        return val
          ? <span className="text-xs text-muted-foreground">{format(new Date(val), "MMM dd, yyyy")}</span>
          : "—";
      },
    },
  ];

  // AFF-10: Add search and status filter
  const tableConfig: DataTableConfig = {
    searchFields: [
      { label: "Name",  field: "name",  placeholder: "Search by name..."  },
      { label: "Email", field: "email", placeholder: "Search by email..." },
    ],
    filterFields: [
      {
        label: "Status",
        field: "status",
        values: [
          { val: "1", name: "Active" },
          { val: "0", name: "Inactive" },
        ],
      },
    ],
    customButtons: [
      { label: "Click Report",      onClick: () => router.push("/admin/affiliate/clicks") },
      { label: "Conversion Report", onClick: () => router.push("/admin/affiliate/conversions") },
    ],
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="h-7 w-1 rounded-full shrink-0" style={{ background: "linear-gradient(to bottom, #ffa800, #ff7e00)" }} />
        <div>
          <h1 className="text-xl font-semibold text-foreground">Affiliate Dashboard</h1>
          <p className="text-xs text-muted-foreground mt-0.5">Your referral performance and earnings overview</p>
        </div>
      </div>

      {/* AFF-02: Copy referral links */}
      {(userId || affiliateId) && (
        <Card style={{ borderColor: "hsl(240 20% 22%)" }}>
          <CardHeader className="border-b py-3 px-5" style={{ borderColor: "hsl(240 20% 20%)" }}>
            <CardTitle className="text-sm flex items-center gap-2">
              <span className="h-3.5 w-0.5 rounded-full" style={{ background: "#ffa800" }} />
              Your Referral Links
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 flex flex-wrap gap-3">
            {userId && (
              <CopyLinkButton
                label="Diviner Signup Link"
                value={`${frontendUrl}payment/${userId}`}
              />
            )}
            {affiliateId && (
              <CopyLinkButton
                label="Affiliate ID"
                value={affiliateId}
              />
            )}
          </CardContent>
        </Card>
      )}

      {/* AFF-08/AFF-09: Stats with correct field mapping + numberOfDiviners card */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <StatCard
          title="Total Sales"
          value={stats?.no_of_Sales ?? stats?.result}
          icon={TrendingUp}
          color="#ffa800"
        />
        <StatCard
          title="Total Revenue"
          value={stats?.totalAmount}
          icon={DollarSign}
          prefix="$"
          color="#16a085"
        />
        <StatCard
          title="Total Clicks"
          value={stats?.number_of_click ?? stats?.totalClick}
          icon={MousePointerClick}
          color="#25aceb"
        />
        <StatCard
          title="Conversions"
          value={stats?.number_of_convertion ?? stats?.totalConversion}
          icon={Award}
          color="#7C3AED"
        />
        <StatCard
          title="Commission"
          value={stats?.tottal_commission ?? stats?.totalCommission}
          icon={DollarSign}
          prefix="$"
          color="#e94a49"
        />
        {/* AFF-09: numberOfDiviners metric card */}
        <StatCard
          title="No. of Diviners"
          value={stats?.numberOfDiviners ?? stats?.number_of_diviner}
          icon={Star}
          color="#ffa800"
        />
      </div>

      <Card style={{ borderColor: "hsl(240 20% 22%)" }}>
        <CardHeader className="border-b py-3 px-5" style={{ borderColor: "hsl(240 20% 20%)" }}>
          <CardTitle className="flex items-center gap-2 text-base">
            <span className="h-4 w-0.5 rounded-full" style={{ background: "#ffa800" }} />
            <Users className="size-4" /> Referred Users
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          {isLoading ? (
            <div className="py-8 text-center text-muted-foreground text-sm">Loading referred users...</div>
          ) : (
            <DataTable
              columns={columns}
              data={referredUsers?.data ?? []}
              totalCount={referredUsers?.count ?? 0}
              config={tableConfig}
              page={page}
              limit={limit}
              onPageChange={setPage}
              onLimitChange={setLimit}
              onSearch={handleSearch}
              onSort={handleSort}
              onFilter={handleFilter}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
